import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DocumentSearchParams,
  UpdateDocumentRequest,
  CreateReferralRequest,
} from '@vagusx/clients';
import { getDocumentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Create a referral document (and link/create its patient) — the "New referral"
 * form. Invalidates the documents list so the new row appears.
 */
export function useCreateReferral() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateReferralRequest) => getDocumentClient().createReferral(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

/**
 * Hook to search document extractions.
 */
export function useDocuments(filters?: DocumentSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.documents.list(filters as Record<string, unknown> | undefined),
    queryFn: () => getDocumentClient().search(filters),
    enabled: options?.enabled,
  });
}

/**
 * Hook to fetch a single document extraction by ID.
 */
export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.detail(id ?? ''),
    queryFn: () => getDocumentClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Get the API streaming URL for a document extraction's PDF.
 * No signed URLs — PDF is streamed through the API.
 */
export function getDocumentPdfUrl(id: string): string {
  return getDocumentClient().getPdfStreamUrl(id);
}

/**
 * Hook to update document extraction fields.
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDocumentRequest }) =>
      getDocumentClient().update(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

/**
 * Hook to update review status.
 */
export function useUpdateReviewStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewStatus }: { id: string; reviewStatus: string }) =>
      getDocumentClient().updateReviewStatus(id, reviewStatus),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

/**
 * Hook to match/unmatch patient.
 */
export function useMatchPatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patientId }: { id: string; patientId: string }) =>
      getDocumentClient().matchPatient(id, patientId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
    },
  });
}

export function useUnmatchPatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getDocumentClient().unmatchPatient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
    },
  });
}

/**
 * Create a new patient in the EHR from the document's extracted demographics and
 * link it (ADR-089). Invalidates the document detail and the patients list so the
 * new patient appears. Errors (unsupported EHR, missing name/DOB, undeterminable
 * location) surface via the global mutation error handler.
 */
export function useCreatePatientFromDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getDocumentClient().createPatientFromDocument(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
    },
  });
}

/**
 * Hook to match/unmatch referring provider.
 */
export function useMatchReferringProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entityId }: { id: string; entityId: string }) =>
      getDocumentClient().matchReferringProvider(id, entityId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
    },
  });
}

/**
 * Hook to match/unmatch rendering provider.
 */
export function useMatchRenderingProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, providerId }: { id: string; providerId: string }) =>
      getDocumentClient().matchRenderingProvider(id, providerId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
    },
  });
}

export function useUnmatchRenderingProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getDocumentClient().unmatchRenderingProvider(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
    },
  });
}

/**
 * Hook to match/unmatch payer.
 */
export function useMatchPayer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entityId }: { id: string; entityId: string }) =>
      getDocumentClient().matchPayer(id, entityId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) });
    },
  });
}
